import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const EstruturaDoc: React.FC = () => {
  const estruturaCode = `smartentrega-web/
├── entregas/ # Pasta principal do projeto React
│ ├── public/ # Arquivos públicos (HTML, ícones, etc.)
│ ├── src/ # Código fonte da aplicação
│ │ ├── components/ # Componentes reutilizáveis
│ │ ├── containers/ # Componentes que gerenciam estado
│ │ ├── hooks/ # Hooks personalizados
│ │ ├── pages/ # Páginas da aplicação
│ │ ├── routes/ # Configuração de rotas
│ │ ├── services/ # Serviços e APIs
│ │ ├── store/ # Configuração do Redux
│ │ │ └── slices/ # Slices do Redux (estado e ações)
│ │ ├── App.tsx # Componente principal da aplicação
│ │ ├── index.tsx # Ponto de entrada da aplicação
│ │ └── ...
│ ├── package.json # Dependências e scripts
│ └── tsconfig.json # Configuração do TypeScript
└── ...`;

  return (
    <DocContainer>
      <Title>Estrutura do Projeto SmartEntrega</Title>

      <Paragraph>
        Este documento explica a organização de pastas e arquivos do projeto
        SmartEntrega, ajudando você a entender onde encontrar e como organizar o
        código.
      </Paragraph>

      <SubTitle>Visão Geral da Estrutura</SubTitle>
      <CodeSnippet code={estruturaCode} />

      <SubTitle>Detalhamento das Pastas</SubTitle>

      <SubTitle>/public</SubTitle>
      <Paragraph>
        Contém arquivos estáticos que são servidos diretamente pelo servidor
        web:
      </Paragraph>
      <List>
        <ListItem>
          <Code>index.html</Code>: Template HTML principal
        </ListItem>
        <ListItem>Imagens, ícones e outros recursos estáticos</ListItem>
      </List>

      <SubTitle>/src</SubTitle>
      <Paragraph>Contém todo o código fonte da aplicação:</Paragraph>

      <SubTitle>/components</SubTitle>
      <Paragraph>
        Componentes React reutilizáveis que não gerenciam estado próprio ou
        lógica de negócio complexa. Exemplos:
      </Paragraph>
      <List>
        <ListItem>Botões</ListItem>
        <ListItem>Inputs</ListItem>
        <ListItem>Cards</ListItem>
        <ListItem>Layout</ListItem>
      </List>

      <SubTitle>/containers</SubTitle>
      <Paragraph>
        Componentes que gerenciam estado e lógica de negócio mais complexa.
        Geralmente, conectam-se ao Redux e passam dados para componentes de
        apresentação.
      </Paragraph>

      <SubTitle>/hooks</SubTitle>
      <Paragraph>
        Hooks personalizados que encapsulam lógica reutilizável:
      </Paragraph>
      <List>
        <ListItem>Manipulação de formulários</ListItem>
        <ListItem>Chamadas de API</ListItem>
        <ListItem>Lógica de autenticação</ListItem>
      </List>

      <SubTitle>/pages</SubTitle>
      <Paragraph>
        Componentes que representam páginas inteiras da aplicação:
      </Paragraph>
      <List>
        <ListItem>
          <Code>Login.tsx</Code>
        </ListItem>
        <ListItem>
          <Code>Dashboard.tsx</Code>
        </ListItem>
        <ListItem>
          <Code>Cadastros.tsx</Code>
        </ListItem>
        <ListItem>
          <Code>Relatorios.tsx</Code>
        </ListItem>
        <ListItem>
          <Code>Documentacao.tsx</Code>
        </ListItem>
      </List>

      <SubTitle>/routes</SubTitle>
      <Paragraph>Configuração do sistema de rotas da aplicação:</Paragraph>
      <List>
        <ListItem>
          <Code>index.tsx</Code>: Define todas as rotas disponíveis
        </ListItem>
      </List>

      <SubTitle>/services</SubTitle>
      <Paragraph>
        Serviços para comunicação com APIs externas e outras funcionalidades:
      </Paragraph>
      <List>
        <ListItem>
          <Code>api.ts</Code>: Configuração e métodos para chamadas de API
        </ListItem>
      </List>

      <SubTitle>/store</SubTitle>
      <Paragraph>Configuração do Redux para gerenciamento de estado:</Paragraph>
      <List>
        <ListItem>
          <Code>index.ts</Code>: Configuração da store
        </ListItem>
        <ListItem>
          <Code>/slices</Code>: Slices do Redux (estado, reducers e actions)
        </ListItem>
        <ListItem>
          <Code>authSlice.ts</Code>: Gerenciamento de autenticação
        </ListItem>
      </List>

      <SubTitle>Arquivos Principais</SubTitle>
      <List>
        <ListItem>
          <Code>App.tsx</Code>: Componente raiz que configura o Provider do
          Redux e as rotas
        </ListItem>
        <ListItem>
          <Code>index.tsx</Code>: Ponto de entrada que renderiza o App no DOM
        </ListItem>
        <ListItem>
          <Code>package.json</Code>: Lista de dependências e scripts
        </ListItem>
        <ListItem>
          <Code>tsconfig.json</Code>: Configuração do TypeScript
        </ListItem>
      </List>

      <SubTitle>Como Navegar pelo Código</SubTitle>
      <OrderedList>
        <OrderedListItem>
          Comece pelo <Code>index.tsx</Code> para entender o ponto de entrada
        </OrderedListItem>
        <OrderedListItem>
          Veja o <Code>App.tsx</Code> para entender a configuração inicial
        </OrderedListItem>
        <OrderedListItem>
          Explore a pasta <Code>routes</Code> para entender a navegação
        </OrderedListItem>
        <OrderedListItem>
          Examine as páginas em <Code>pages</Code> para ver os componentes
          principais
        </OrderedListItem>
        <OrderedListItem>
          Explore os componentes em <Code>components</Code> para entender os
          blocos de construção
        </OrderedListItem>
      </OrderedList>
    </DocContainer>
  );
};

export default EstruturaDoc;
